import { useState } from "react"
import { useToast } from "@/hooks/use-toast"
import { DocumentFormFields } from "../DocumentFormFields"
import { Button } from "../ui/button"
import { supabase } from "@/integrations/supabase/client"
import { useQuery } from "@tanstack/react-query"
import { DocumentPreviewDialog } from "./DocumentPreviewDialog"
import { useDocumentGeneration } from "@/hooks/useDocumentGeneration"
import { motion } from "framer-motion"

interface DocumentFormContainerProps {
  type: "oficio" | "requerimento" | "discurso" | "memorando"
  onDocumentGenerated?: (text: string) => void
}

export function DocumentFormContainer({ type, onDocumentGenerated }: DocumentFormContainerProps) {
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [generatedContent, setGeneratedContent] = useState<string | null>(null)
  const { generateDocument } = useDocumentGeneration()
  const [formData, setFormData] = useState({
    subject: "",
    content: "",
    recipient: "",
    officeType: "",
    requestType: "",
    eventType: "",
    targetAudience: "",
    speechObjective: "",
  })

  const { data: profile } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error("User not found")

      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single()

      if (error) throw error
      return data
    },
  })

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const resetForm = () => {
    setFormData({
      subject: "",
      content: "",
      recipient: "",
      officeType: "",
      requestType: "",
      eventType: "",
      targetAudience: "",
      speechObjective: "",
    })
  }

  const validateFields = () => {
    if (type === "discurso") {
      if (!formData.eventType || !formData.targetAudience || !formData.speechObjective || !formData.content) {
        toast({
          title: "Campos obrigatórios",
          description: "Por favor, preencha todos os campos obrigatórios do discurso.",
          variant: "destructive",
        })
        return false
      }
    } else if (type === "oficio") {
      if (!formData.officeType || !formData.content) {
        toast({
          title: "Campos obrigatórios",
          description: "Por favor, preencha todos os campos obrigatórios do ofício.",
          variant: "destructive",
        })
        return false
      }
    } else if (type === "requerimento") {
      if (!formData.requestType || !formData.content) {
        toast({
          title: "Campos obrigatórios",
          description: "Por favor, preencha todos os campos obrigatórios do requerimento.",
          variant: "destructive",
        })
        return false
      }
    } else if (type === "memorando") {
      if (!formData.content) {
        toast({
          title: "Campos obrigatórios",
          description: "Por favor, preencha todos os campos obrigatórios do memorando.",
          variant: "destructive",
        })
        return false
      }
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateFields()) {
      return
    }

    setLoading(true)

    try {
      const result = await generateDocument({ type, formData, profile })
      setGeneratedContent(result)
      onDocumentGenerated?.(result)
      resetForm()
      toast({
        title: "Documento gerado com sucesso!",
        description: "O documento foi gerado e os campos foram limpos.",
      })
    } catch (error) {
      console.error("Erro ao gerar documento:", error)
      toast({
        title: "Erro ao gerar documento",
        description: "Ocorreu um erro ao gerar o documento. Por favor, tente novamente.",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCloseDialog = () => {
    setLoading(false)
    setGeneratedContent(null)
  }

  const handleContentChange = (newContent: string) => {
    setGeneratedContent(newContent)
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white/50 backdrop-blur-sm rounded-lg p-6 shadow-lg border border-gray-100">
          <DocumentFormFields
            type={type}
            formData={formData}
            onChange={handleChange}
          />
        </div>

        <motion.div
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
        >
          <Button 
            type="submit" 
            className="w-full bg-primary hover:bg-primary/90 text-white font-medium py-6 rounded-xl shadow-lg shadow-primary/20 transition-all duration-300 relative overflow-hidden group"
            disabled={loading}
          >
            <span className="relative z-10 flex items-center justify-center gap-2 text-base font-semibold">
              {loading ? "Gerando documento..." : "Gerar documento"}
              {!loading && (
                <motion.span
                  initial={{ x: -5, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                >
                  →
                </motion.span>
              )}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300" />
          </Button>
        </motion.div>
      </form>

      <DocumentPreviewDialog
        isOpen={loading || !!generatedContent}
        onClose={handleCloseDialog}
        content={generatedContent}
        loading={loading}
        onContentChange={handleContentChange}
      />
    </div>
  )
}
